import type { Dispute } from "../../features/disputes/types/disputes.types"
import { mockCustomers } from "./mockCustomers"
import { mockSeedPayments } from "./mockSeedPayments"

// Helpers — derive customer and paymentId from shared sources so all three
// datasets (payments, refunds, disputes) stay in sync.
const cust = (id: string) => {
  const c = mockCustomers.find((c) => c.id === id)!
  return { id: c.id, name: c.name, email: c.email }
}

const payId = (id: string) => {
  const p = mockSeedPayments.find((p) => p.id === id)
  if (!p) throw new Error(`mockDisputes references unknown seed payment: ${id}`)
  return p.id
}

export const mockDisputes: Dispute[] = [
  {
    id: "dp_1234567890",
    amount: 8000,
    currency: "BGN",
    decimal: 2,
    status: "needs_response",
    paymentId: payId("8a6e7a2d-0c91-4d44-bb0e-5cf4906c6a45"),
    reason: "fraudulent",
    created: new Date("2025-06-19T10:30:00Z"),
    customer: cust("cust_006"),
    evidenceDueBy: new Date("2025-06-26T23:59:59Z"),
    lastUpdated: new Date("2025-06-19T10:35:00Z"),
  },
  {
    id: "dp_0987654321",
    amount: 500,
    currency: "BGN",
    decimal: 2,
    status: "under_review",
    paymentId: payId("0ff165ee-6035-47e0-849c-530302075189"),
    reason: "product_not_received",
    created: new Date("2025-06-20T15:45:00Z"),
    customer: cust("cust_002"),
    lastUpdated: new Date("2025-06-20T15:50:00Z"),
  },
  {
    id: "dp_1122334455",
    amount: 1000,
    currency: "BGN",
    decimal: 2,
    status: "won",
    paymentId: payId("12ba604c-703a-4cca-85c1-dc77fea3e365"),
    reason: "duplicate",
    created: new Date("2025-06-21T09:15:00Z"),
    customer: cust("cust_005"),
    lastUpdated: new Date("2025-06-21T10:20:00Z"),
  },
  {
    id: "dp_5566778899",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "lost",
    paymentId: payId("23ba9cc9-3350-46cb-88e6-7027f669342c"),
    reason: "subscription_canceled",
    created: new Date("2025-06-22T14:20:00Z"),
    customer: cust("cust_010"),
    lastUpdated: new Date("2025-06-22T15:30:00Z"),
  },
  {
    id: "dp_9988776655",
    amount: 1000,
    currency: "BGN",
    decimal: 2,
    status: "warning_needs_response",
    paymentId: payId("0541af14-f40c-4c63-8120-3de24687501f"),
    reason: "unrecognized",
    created: new Date("2025-06-23T11:00:00Z"),
    customer: cust("cust_004"),
    evidenceDueBy: new Date("2025-06-30T23:59:59Z"),
    lastUpdated: new Date("2025-06-23T11:05:00Z"),
  },
  {
    id: "dp_high_value",
    amount: 12541,
    currency: "BGN",
    decimal: 2,
    status: "charge_refunded",
    paymentId: payId("237ce88c-758e-4048-9ace-756486929296"),
    reason: "product_unacceptable",
    created: new Date("2025-06-24T16:30:00Z"),
    customer: cust("cust_013"),
    lastUpdated: new Date("2025-06-24T16:45:00Z"),
  },
  {
    id: "dp_refund_case",
    amount: 2000,
    currency: "BGN",
    decimal: 2,
    status: "warning_under_review",
    paymentId: payId("6f2aa264-b240-4e87-8ac5-f72ee400d643"),
    reason: "credit_not_processed",
    created: new Date("2025-06-25T08:15:00Z"),
    customer: cust("cust_001"),
    lastUpdated: new Date("2025-06-25T08:20:00Z"),
  },
  {
    id: "dp_terminal_01",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "needs_response",
    paymentId: payId("3d45e9ac-9c9f-4208-ab08-2bb6228ada27"),
    reason: "fraudulent",
    created: new Date("2025-06-26T09:15:00Z"),
    customer: cust("cust_014"),
    evidenceDueBy: new Date("2025-07-03T23:59:59Z"),
    lastUpdated: new Date("2025-06-26T09:20:00Z"),
  },
  {
    id: "dp_partial_refund",
    amount: 1000,
    currency: "BGN",
    decimal: 2,
    status: "under_review",
    paymentId: payId("fe4b2176-f496-4398-a633-84a81e76394f"),
    reason: "product_unacceptable",
    created: new Date("2025-06-27T12:30:00Z"),
    customer: cust("cust_009"),
    lastUpdated: new Date("2025-06-27T12:35:00Z"),
  },
  {
    id: "dp_verification",
    amount: 0,
    currency: "BGN",
    decimal: 2,
    status: "won",
    paymentId: payId("a4c49a44-e5ec-49c2-81fb-02f59518714f"),
    reason: "unrecognized",
    created: new Date("2025-06-28T14:45:00Z"),
    customer: cust("cust_011"),
    lastUpdated: new Date("2025-06-28T15:00:00Z"),
  },
  {
    id: "dp_auth_check",
    amount: 0,
    currency: "BGN",
    decimal: 2,
    status: "warning_closed",
    paymentId: payId("8ae06527-f5f4-43d5-8836-8f6d5fd04aff"),
    reason: "general",
    created: new Date("2025-06-29T10:20:00Z"),
    customer: cust("cust_012"),
    lastUpdated: new Date("2025-06-29T10:25:00Z"),
  },
  {
    id: "dp_purchase_01",
    amount: 1200,
    currency: "BGN",
    decimal: 2,
    status: "charge_refunded",
    paymentId: payId("ed600cf8-3d7e-477f-9986-0afe0c909a33"),
    reason: "product_not_received",
    created: new Date("2025-06-30T16:10:00Z"),
    customer: cust("cust_003"),
    lastUpdated: new Date("2025-06-30T16:30:00Z"),
  },
  {
    id: "dp_purchase_02",
    amount: 950,
    currency: "BGN",
    decimal: 2,
    status: "lost",
    paymentId: payId("49bc4429-e793-4bb5-9db3-902ee0e72fd9"),
    reason: "subscription_canceled",
    created: new Date("2025-07-01T08:45:00Z"),
    customer: cust("cust_015"),
    lastUpdated: new Date("2025-07-01T09:15:00Z"),
  },
  {
    id: "dp_preauth_01",
    amount: 12541,
    currency: "BGN",
    decimal: 2,
    status: "needs_response",
    paymentId: payId("237ce88c-758e-4048-9ace-756486929296"),
    reason: "duplicate",
    created: new Date("2025-07-02T11:20:00Z"),
    customer: cust("cust_013"),
    evidenceDueBy: new Date("2025-07-09T23:59:59Z"),
    lastUpdated: new Date("2025-07-02T11:25:00Z"),
  },
  {
    id: "dp_terminal_02",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "warning_needs_response",
    paymentId: payId("01e197c9-662b-42c8-a89c-82f6999149d3"),
    reason: "unrecognized",
    created: new Date("2025-07-03T13:50:00Z"),
    customer: cust("cust_018"),
    evidenceDueBy: new Date("2025-07-10T23:59:59Z"),
    lastUpdated: new Date("2025-07-03T14:00:00Z"),
  },
  {
    id: "dp_capture_01",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "under_review",
    paymentId: payId("e05c8ed0-8e50-4c27-af55-2f00f52c997b"),
    reason: "credit_not_processed",
    created: new Date("2025-07-04T15:30:00Z"),
    customer: cust("cust_016"),
    lastUpdated: new Date("2025-07-04T15:45:00Z"),
  },
  {
    id: "dp_verify_02",
    amount: 0,
    currency: "BGN",
    decimal: 2,
    status: "won",
    paymentId: payId("12add6bb-2c2b-477c-adb0-c46dad366ff4"),
    reason: "general",
    created: new Date("2025-07-05T09:10:00Z"),
    customer: cust("cust_017"),
    lastUpdated: new Date("2025-07-05T09:30:00Z"),
  },
  {
    id: "dp_preauth_02",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "charge_refunded",
    paymentId: payId("df83920d-38b9-457e-8034-7b5e0e92dd3a"),
    reason: "product_unacceptable",
    created: new Date("2025-07-06T12:45:00Z"),
    customer: cust("cust_007"),
    lastUpdated: new Date("2025-07-06T13:10:00Z"),
  },
  {
    id: "dp_token_payment",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "warning_under_review",
    paymentId: payId("78b1e4c5-079c-4389-b7b1-ef27c25254aa"),
    reason: "fraudulent",
    created: new Date("2025-07-07T14:20:00Z"),
    customer: cust("cust_019"),
    lastUpdated: new Date("2025-07-07T14:40:00Z"),
  },
  {
    id: "dp_terminal_03",
    amount: 4000,
    currency: "BGN",
    decimal: 2,
    status: "lost",
    paymentId: payId("1a054460-487b-4bdd-af5e-e299126252cf"),
    reason: "duplicate",
    created: new Date("2025-07-08T16:00:00Z"),
    customer: cust("cust_020"),
    lastUpdated: new Date("2025-07-08T16:30:00Z"),
  },
  {
    id: "dp_capture_02",
    amount: 4000,
    currency: "BGN",
    decimal: 2,
    status: "needs_response",
    paymentId: payId("a7f7c1ec-ef29-43e0-833f-359c268a4f36"),
    reason: "product_not_received",
    created: new Date("2025-07-09T10:15:00Z"),
    customer: cust("cust_009"),
    evidenceDueBy: new Date("2025-07-16T23:59:59Z"),
    lastUpdated: new Date("2025-07-09T10:25:00Z"),
  },
  {
    id: "dp_verify_03",
    amount: 0,
    currency: "BGN",
    decimal: 2,
    status: "warning_closed",
    paymentId: payId("e06781fd-1551-45ee-bdcf-79bd97c8c22a"),
    reason: "unrecognized",
    created: new Date("2025-07-10T11:45:00Z"),
    customer: cust("cust_011"),
    lastUpdated: new Date("2025-07-10T12:00:00Z"),
  },
  {
    id: "dp_void_transaction",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "won",
    paymentId: payId("6ac51fe2-09d6-4bc7-bb7d-234634480344"),
    reason: "subscription_canceled",
    created: new Date("2025-07-11T13:20:00Z"),
    customer: cust("cust_012"),
    lastUpdated: new Date("2025-07-11T13:45:00Z"),
  },
  {
    id: "dp_refund_void",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "under_review",
    paymentId: payId("1237012e-bd75-4ef4-be7f-6660d50656d2"),
    reason: "credit_not_processed",
    created: new Date("2025-07-12T08:30:00Z"),
    customer: cust("cust_015"),
    lastUpdated: new Date("2025-07-12T08:50:00Z"),
  },
  {
    id: "dp_preauth_03",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "charge_refunded",
    paymentId: payId("a85d4e0e-89bf-4157-b928-f259f388bb26"),
    reason: "general",
    created: new Date("2025-07-13T15:10:00Z"),
    customer: cust("cust_018"),
    lastUpdated: new Date("2025-07-13T15:30:00Z"),
  },
]
